import type { useClassroom } from "./useClassroom";

type ClassroomState = ReturnType<typeof useClassroom>;
type DeliveryMode = "hybrid" | "all-live";
type PlaybackProps = {
  readonly classroom: ClassroomState;
  readonly mode: DeliveryMode;
  readonly scriptVisible: boolean;
  readonly onAsk: () => void;
  readonly onShowScript: () => void;
};

export function ClassroomPlayback({
  classroom,
  mode,
  scriptVisible,
  onAsk,
  onShowScript,
}: PlaybackProps) {
  const { demo, session, pending } = classroom;
  if (!demo) return null;
  const lesson = session?.lesson ?? demo.lesson;
  const completed = session?.committedCursor.phraseIndex ?? 0;
  const phrase = lesson.phrases[completed];
  const progress = Math.round((completed / lesson.phrases.length) * 100);
  const active = session !== null && session.status !== "complete";
  const reading = session?.status === "narrating";
  const canAsk = session && ["narrating", "answering", "listening"].includes(session.status);
  const canResume = session && ["paused", "resuming", "listening"].includes(session.status);
  return (
    <section className="playback-panel" aria-label="Lesson controls">
      <div className="progress-label">
        <span>
          {completed} of {lesson.phrases.length} phrases confirmed read
        </span>
        <span>{progress}%</span>
      </div>
      <progress
        value={completed}
        max={lesson.phrases.length}
        aria-label="Confirmed reading progress"
      />
      <div className="playback-controls">
        <div className="cluster">
          {!active ? (
            <button
              type="button"
              className="button primary"
              disabled={pending || !demo.readiness.rehearsal.available}
              onClick={() => void classroom.start(mode)}
            >
              Play rehearsal
            </button>
          ) : reading ? (
            <button
              type="button"
              className="button"
              disabled={pending}
              onClick={() => void classroom.action({ type: "pause" })}
            >
              Pause
            </button>
          ) : (
            <button
              type="button"
              className="button primary"
              disabled={pending || !canResume}
              onClick={() => void classroom.action({ type: "resume" })}
            >
              Resume lesson
            </button>
          )}
          <button
            type="button"
            className="button"
            disabled={pending || !canAsk}
            onClick={() => {
              onAsk();
              void classroom.interrupt();
            }}
          >
            Ask / Interrupt
          </button>
          {!scriptVisible && (
            <button type="button" className="button quiet" onClick={() => onShowScript()}>
              Show script
            </button>
          )}
        </div>
        <span className="mic-state">Microphone off</span>
      </div>
      {reading && phrase && (
        <div className="read-confirm">
          <p lang="ko">{phrase.text}</p>
          <button
            type="button"
            className="button primary"
            disabled={pending}
            onClick={() =>
              void classroom.action({
                type: "playback",
                phraseId: phrase.id,
                textOffset: phrase.endOffset,
                completed: true,
                playbackConfirmed: true,
              })
            }
          >
            Confirm phrase read
          </button>
        </div>
      )}
      {session?.reentryPlan && (
        <p className="resume-note">
          Returning to phrase {session.reentryPlan.phraseId ? completed + 1 : lesson.phrases.length}
          . Confirmed reading has been preserved.
        </p>
      )}
    </section>
  );
}

type OptionsProps = {
  readonly classroom: ClassroomState;
  readonly mode: DeliveryMode;
  readonly onModeChange: (mode: DeliveryMode) => void;
};

export function ClassroomOptions({ classroom, mode, onModeChange }: OptionsProps) {
  const { session, pending } = classroom;
  const active = session !== null && session.status !== "complete";
  return (
    <details className="lesson-options">
      <summary>Lesson options</summary>
      <div className="stack">
        <label className="field">
          Delivery mode
          <select
            value={mode}
            disabled={active}
            onChange={(event) =>
              onModeChange(event.target.value === "all-live" ? "all-live" : "hybrid")
            }
          >
            <option value="hybrid">Hybrid course pack</option>
            <option value="all-live">All-live pilot</option>
          </select>
          <span className="muted">Both paths use text rehearsal until live media is ready.</span>
        </label>
        <div className="cluster">
          <button type="button" className="button" disabled title="Live media is not configured">
            Play live · Unavailable
          </button>
          {active && (
            <>
              <button
                type="button"
                className="button"
                disabled={pending}
                onClick={() => void classroom.start(mode)}
              >
                Restart lesson
              </button>
              <button
                type="button"
                className="button danger"
                disabled={pending}
                onClick={() => void classroom.action({ type: "end" })}
              >
                End session
              </button>
            </>
          )}
        </div>
        <p className="muted">
          This classroom uses an AI instructor. Rehearsal has no recorded or generated voice, video,
          or microphone input.
        </p>
      </div>
    </details>
  );
}
